import { useEffect, useState } from 'react';

import type { PublicMaintenanceStatusResponse } from '@shared/admin';
import type { ResponseEnvelope } from '@shared/envelope';

/**
 * Polling 60s `/api/v1/maintenance-status` public unauth endpoint (B13).
 *
 * Returnează `{is_active, message}` actualizat la fiecare 60s. Folosit de
 * `MaintenanceModal` care randează modal non-dismissible peste tot UI-ul
 * dacă is_active=true.
 *
 * Strategy: silent fail dacă endpoint inaccesibil — nu vrem să spam UI cu erori
 * dacă serverul e down (în care caz user-ul are deja probleme mai mari).
 */
const POLL_INTERVAL_MS = 60_000;

export interface MaintenanceStatus {
  isActive: boolean;
  message: string | null;
}

export function useMaintenanceStatus(): MaintenanceStatus {
  const [status, setStatus] = useState<MaintenanceStatus>({ isActive: false, message: null });

  useEffect(() => {
    let cancelled = false;
    const fetchStatus = async (): Promise<void> => {
      try {
        const resp = await fetch('/api/v1/maintenance-status', { credentials: 'same-origin' });
        if (!resp.ok) return;
        const envelope = (await resp.json()) as ResponseEnvelope<PublicMaintenanceStatusResponse>;
        if (cancelled || !envelope.success || !envelope.data) return;
        setStatus({ isActive: envelope.data.is_active, message: envelope.data.message });
      } catch {
        // silent — fail-open (user can still try to use app)
      }
    };
    void fetchStatus();
    const interval = setInterval(() => void fetchStatus(), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  return status;
}
